'use client';

import styled from '@emotion/styled';
import axios from 'axios';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import type { CSSObjectWithLabel } from 'react-select';
import Select, { ControlProps } from 'react-select';
import * as Yup from 'yup';
import { ProposalType } from '@diritto/common/chat/constants';
import { PRIVACY_URL } from '@diritto/common/constants/url';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ChatPartnerApi } from 'api/ApiHelper';
import { ContentTextArea } from 'app-components/contents/editor/ContentTextArea';
import { ButtonType } from 'constants/button';
import { isPC } from 'constants/layout';
import { ModalParams, ModalType } from 'constants/modal';
import { Button } from 'ui-components/common/Button';
import { OptionType, SelectStyle } from 'ui-components/common/CreatableSelectBox';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { ResizableCheckBox } from 'ui-components/common/ResizableCheckBox';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { useShowModal } from 'ui-hooks/useShowModal';
import { showToast } from 'utils/modal';
import { ModalHandler } from 'utils/ModalHandler';
import { SentryService } from 'utils/SentryService';

const EditContentTextArea = React.memo(ContentTextArea);

interface ContractProposalFormType {
  message: string;
  type?: ProposalType;
  agreeToUsePersonalInfo: boolean;
}

export const ProposalTypeOptions = [
  {
    value: ProposalType.Publication,
    label: '출간 · 기고',
  },
  {
    value: ProposalType.Etc,
    label: '기타',
  },
];

export const ContractProposalModal = () => {
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.ContractProposal]>();

  const validationSchema = Yup.object().shape({
    message: Yup.string().required('내용을 입력해주세요.'),
    type: Yup.string().required('제안 항목을 선택해주세요.'),
    agreeToUsePersonalInfo: Yup.boolean().oneOf([true], '개인정보 수집 및 이용에 동의해주세요.'),
  });

  const initialValues = {
    message: '',
    type: undefined,
    agreeToUsePersonalInfo: false,
  };

  const handlePostMessage = useCallback(
    async (params: ContractProposalFormType) => {
      if (!state) {
        return;
      }

      if (!params.type) {
        showToast('제안 항목을 선택해주세요.');
      }

      try {
        await ChatPartnerApi.postChannel({
          contentId: state.contentId,
          proposalType: params.type as ProposalType,
          message: params.message,
        });

        showToast('작가에게 제안이 보내졌어요.');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          return;
        }
        SentryService.captureException(e);
        showToast('알 수 없는 문제가 발생했어요. 잠시 뒤 다시 시도해주세요.');
      }
    },
    [state],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: values => {
      handlePostMessage(values).then(() => {
        hideModal();
      });
    },
  });

  const handleSetValue = useCallback(
    (field: string, value: any) => {
      formik.setFieldValue(field, value);
      formik.setFieldTouched(field, true);
    },
    [formik],
  );

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    hideModal();
  }, [hideModal, state]);

  const handleConfirm = useCallback(() => {
    state?.onConfirm?.();
    formik.handleSubmit();
    hideModal();
  }, [hideModal, state, formik]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.ContractProposal]) => {
      if (show) {
        showModal();
      } else {
        hideModal();
      }
      setState(params);
    };

    ModalHandler.addEventListener(ModalType.ContractProposal, listener);
    return () => {
      ModalHandler.removeEventListener(ModalType.ContractProposal);
    };
  }, [hideModal, showModal]);

  const { isPc } = useMedia();

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
        <ModalLayout isShow={isShow} revealModal={revealModal} isBottom={!isPc}>
          <StyledModal>
            <StyledTitle>작가에게 제안하기</StyledTitle>
            <StyledWrapper>
              <StyledSubject>작품명</StyledSubject>
              <StyledDescription>{state?.title}</StyledDescription>
            </StyledWrapper>
            <StyledWrapper>
              <StyledSubject>제안 항목 (필수)</StyledSubject>
              <StyledSelectWrapper>
                <Select<OptionType>
                  id="type"
                  options={ProposalTypeOptions}
                  placeholder="선택"
                  isSearchable={false}
                  value={ProposalTypeOptions.find(it => it.value === formik.values.type)}
                  onChange={(option: OptionType | null) => handleSetValue('type', option?.value ?? undefined)}
                  styles={customSelectStyles}
                />
              </StyledSelectWrapper>
            </StyledWrapper>
            <StyledWrapper>
              <StyledSubject>내용 (필수)</StyledSubject>
              <StyledEditContentTextArea
                id="message"
                rows={7}
                placeholder="관련된 정보를 상세히 입력해 주세요. 광고/홍보/스팸성 제안하기로 인한 신고 인입 시 서비스 이용이 제한될 수 있어요."
                {...formik.getFieldProps('message')}
              />
            </StyledWrapper>
            <StyledWrapper>
              <ResizableCheckBox
                id="agreeToUsePersonalInfo"
                checked={formik.values.agreeToUsePersonalInfo}
                size={20}
                {...formik.getFieldProps('agreeToUsePersonalInfo')}>
                <StyledAgreePersonalInfoDescription>
                  <ExternalLink href={PRIVACY_URL}>
                    <div style={{ textDecoration: 'underline' }}>개인정보 수집 및 이용</div>
                  </ExternalLink>
                  &nbsp;동의 (필수)
                </StyledAgreePersonalInfoDescription>
              </ResizableCheckBox>
            </StyledWrapper>
            <StyledButtons>
              <StyledButton label="작가에게 제안 보내기" onClick={handleConfirm} disabled={!formik.isValid} />
              <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
            </StyledButtons>
          </StyledModal>
        </ModalLayout>
      </Form>
    </FormikProvider>
  );
};

const StyledAgreePersonalInfoDescription = styled.div`
  display: flex;
  font-weight: ${FontWeight.Regular};
  ${Typography.XXSmall};
`;

const StyledModal = styled.div`
  width: 100vw;
  max-height: 700px;
  background-color: ${Color.White};
  border-radius: 16px 16px 0 0;

  @media (${isPC}) {
    max-width: 390px;
    border-radius: 16px;
    padding: 32px 24px 16px 24px;
  }

  display: flex;
  flex-direction: column;
  padding-top: 32px;
  padding-right: 24px;
  padding-left: 24px;
  ${SafeAreaBottomStyles(16)}
`;

const StyledTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
`;

const StyledDescription = styled.div`
  margin-top: 4px;
  ${Typography.XXSmall};
  color: ${Color.Black};
`;

const StyledButtons = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledSubject = styled.p`
  display: flex;
  align-items: center;
  color: ${Color.Gray600};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
  margin-bottom: 8px;
`;

const StyledWrapper = styled.div`
  margin-top: 16px;
`;

const StyledEditContentTextArea = styled(EditContentTextArea)`
  padding: 13px 16px 14px;
  resize: none;
`;

const StyledSelectWrapper = styled.div`
  display: flex;
  height: 48px;
`;

const customSelectStyles = {
  ...SelectStyle,
  control: (provided: CSSObjectWithLabel, state: ControlProps<OptionType, false>): CSSObjectWithLabel => ({
    ...SelectStyle.control?.(provided, state),
    padding: '0 8px',
  }),
};
